import type { Clip, ClipEdits, Segment, Word } from "./clip.js";
import { buildSegments, cutSilences, pageCaptions, sourceToTimeline } from "./timing.js";
import { buildCropKeyframes, emphasisRuns, findEmphasis } from "./faces.js";
import { punchRuns } from "./direction.js";

const findEmphasisIdx = (words: Word[], emphasis: string[]) => findEmphasis(words, emphasis).flatMap((h) => h.idx);

export interface DeriveOptions { gapMs?: number; padMs?: number }

/** The clip's edits, defaulting to the stored source range for clips saved before edits existed. */
export const editsOf = (clip: Clip): ClipEdits => clip.edits ?? defaultEdits(clip.sourceStartMs, clip.sourceEndMs);

/** Words inside the current trim, with text edits and emphasis applied. */
export function effectiveWords(clip: Clip): Word[] {
  const { trimStartMs, trimEndMs, wordText } = editsOf(clip);
  return clip.words
    .filter((w) => w.endMs > trimStartMs && w.startMs < trimEndMs)
    .map((w) => (wordText[String(w.startMs)] !== undefined ? { ...w, text: wordText[String(w.startMs)] } : w));
}

function subtract(segments: Segment[], cuts: Segment[]): Segment[] {
  let out = segments;
  for (const c of cuts) {
    const next: Segment[] = [];
    for (const s of out) {
      if (c.outMs <= s.inMs || c.inMs >= s.outMs) { next.push(s); continue; }
      if (c.inMs > s.inMs) next.push({ inMs: s.inMs, outMs: c.inMs });
      if (c.outMs < s.outMs) next.push({ inMs: c.outMs, outMs: s.outMs });
    }
    out = next;
  }
  return out.filter((s) => s.outMs - s.inMs >= 120);
}

function union(segments: Segment[], keeps: Segment[]): Segment[] {
  const all = [...segments, ...keeps].sort((a, b) => a.inMs - b.inMs);
  const out: Segment[] = [];
  for (const s of all) {
    const last = out[out.length - 1];
    if (last && s.inMs <= last.outMs) last.outMs = Math.max(last.outMs, s.outMs);
    else out.push({ ...s });
  }
  return out;
}

/** Puts the chronological pieces into the owner's playback order. Entries name pieces by source in-point (nearest match within 2 s, so a nudged edge keeps its place); the same piece may appear more than once; pieces the arrangement does not know (new splits, extensions) slot in next to their chronological neighbours. */
export function arrangeSegments(chrono: Segment[], arrangement: number[]): Segment[] {
  if (!arrangement.length || chrono.length < 2) return chrono;
  const match = (ms: number) => { let best: Segment | null = null; for (const s of chrono) if (Math.abs(s.inMs - ms) <= 2000 && (!best || Math.abs(s.inMs - ms) < Math.abs(best.inMs - ms))) best = s; return best; };
  const out: Segment[] = [];
  for (const ms of arrangement) { const s = match(ms); if (s) out.push(s); }
  if (!out.length) return chrono;
  const known = new Set(out);
  for (const s of chrono) {
    if (known.has(s)) continue;
    const before = [...chrono].reverse().find((x) => known.has(x) && x.inMs < s.inMs);
    if (before) out.splice(out.lastIndexOf(before) + 1, 0, s);
    else { const after = chrono.find((x) => known.has(x) && x.inMs > s.inMs); out.splice(after ? out.indexOf(after) : out.length, 0, s); }
    known.add(s);
  }
  return out;
}

/** Segments from words + silences + manual edits, clamped to the trim, in playback order. */
export function deriveSegments(clip: Clip, opts: DeriveOptions = {}): Segment[] {
  return arrangeSegments(chronoSegments(clip, opts), editsOf(clip).arrangement);
}

/** A kept piece shorter than this is a sliver, not speech. */
export const MIN_PIECE_MS = 300;

/** The kept pieces in source order (before any rearrangement). */
export function chronoSegments(clip: Clip, opts: DeriveOptions = {}): Segment[] {
  const gapMs = opts.gapMs ?? 1000, padMs = opts.padMs ?? 200;
  const { trimStartMs, trimEndMs, manualCuts, keptGaps } = editsOf(clip);
  const words = effectiveWords(clip);
  const auto = cutSilences(buildSegments(words, trimStartMs, trimEndMs, { gapMs, padMs }), clip.silences, { gapMs, padMs });
  const withKeeps = union(auto, keptGaps.filter((k: Segment) => k.outMs > trimStartMs && k.inMs < trimEndMs).map((k: Segment) => ({ inMs: Math.max(k.inMs, trimStartMs), outMs: Math.min(k.outMs, trimEndMs) })));
  // slivers left where a silence cut meets an omitted sentence's cut are not pieces anyone wants
  const cut = subtract(withKeeps, manualCuts).filter((s) => s.outMs - s.inMs >= MIN_PIECE_MS);
  // manual splits: a piece boundary with no gap (the editor selects and deletes pieces between boundaries)
  const splits = editsOf(clip).splits.filter((ms) => ms > trimStartMs && ms < trimEndMs).sort((a, b) => a - b);
  if (!splits.length) return cut;
  const out: Segment[] = [];
  for (const s of cut) {
    let cursor = s.inMs;
    for (const ms of splits) if (ms > cursor && ms < s.outMs) { out.push({ inMs: cursor, outMs: ms }); cursor = ms; }
    out.push({ inMs: cursor, outMs: s.outMs });
  }
  return out.filter((s) => s.outMs - s.inMs >= 40);
}

/** Timeline-mapped words (after cuts) for captions, piece by piece in playback order, so a piece that plays twice is captioned twice. */
export function timelineWords(clip: Clip, segments: Segment[]): Word[] {
  const out: Word[] = [];
  const words = effectiveWords(clip);
  let acc = 0;
  for (const s of segments) {
    for (const w of words) {
      if (!w.text.trim() || w.endMs <= s.inMs || w.startMs >= s.outMs) continue;
      const start = acc + Math.max(0, w.startMs - s.inMs);
      const end = acc + Math.min(s.outMs - s.inMs, w.endMs - s.inMs);
      out.push({ text: w.text, startMs: Math.round(start), endMs: Math.round(Math.max(end, start + 40)), emphasis: w.emphasis || undefined });
    }
    acc += s.outMs - s.inMs;
  }
  return out;
}

/** Recomputes segments, captions and crop from the clip's primitives. Pure and idempotent. */
export function deriveClip(input: Clip, opts: DeriveOptions = {}): Clip {
  const edits = editsOf(input);
  const clip = { ...input, edits };
  const segments = deriveSegments(clip, opts);
  const max = clip.style.wordsPerCard;
  const captions = pageCaptions(timelineWords(clip, segments), { min: Math.min(3, max), max, maxChars: Math.round(max * 5.6), breaksMs: edits.cardBreaks });
  const runs = [...emphasisRuns(effectiveWords(clip)), ...edits.zoomMarks.map((ms) => ({ startMs: ms, endMs: ms + 600, manual: true })), ...punchRuns(clip)];
  const crop = buildCropKeyframes(clip.facePoints, runs, { zoom: clip.zoomStrength, offset: clip.cropOffset, follow: clip.follow, maxZooms: clip.zoomsPerClip });
  const sourceStartMs = segments[0]?.inMs ?? edits.trimStartMs;
  const sourceEndMs = segments[segments.length - 1]?.outMs ?? edits.trimEndMs;
  return { ...clip, segments, captions, crop, sourceStartMs, sourceEndMs };
}

export const defaultEdits = (startMs: number, endMs: number): ClipEdits => ({ trimStartMs: startMs, trimEndMs: endMs, manualCuts: [], keptGaps: [], wordText: {}, cardBreaks: [], zoomMarks: [], splits: [], arrangement: [], musicMutes: [] });

/** Fills the editable primitives for clips created before they existed (older clip.json files). */
export function hydrateClip(clip: Clip, transcriptWords: Word[] | null, emphasisWords: string[] = clip.selection?.emphasisWords ?? []): Clip {
  let out = clip;
  if (!out.edits) out = { ...out, edits: defaultEdits(out.sourceStartMs, out.sourceEndMs) };
  if (!out.words.length && transcriptWords) {
    const words = transcriptWords.filter((w) => w.endMs > out.sourceStartMs - 3000 && w.startMs < out.sourceEndMs + 3000).map((w) => ({ ...w }));
    const hits = new Set(findEmphasisIdx(words, emphasisWords));
    out = { ...out, words: words.map((w, i) => (hits.has(i) ? { ...w, emphasis: true } : w)) };
  }
  if (!out.silences.length && out.segments.length > 1) {
    out = { ...out, silences: out.segments.slice(1).map((s, i) => ({ startMs: out.segments[i].outMs - 120, endMs: s.inMs + 120 })) };
  }
  if (!out.facePoints.length && out.crop.length) {
    out = { ...out, facePoints: out.crop.filter((k) => k.zoom === 1).map((k) => ({ ms: k.ms, cx: k.cx, cy: k.cy, w: 0.1 })) };
  }
  return out;
}

/** The music mute ranges in force: the owner's own plus the detected Quran spans when quranMute is on. */
export function musicMuteRanges(clip: Clip): Segment[] {
  const own = editsOf(clip).musicMutes;
  const directed = clip.direction.moments.filter((m) => m.kind === "quiet").map((m) => ({ inMs: m.atMs, outMs: m.endMs ?? m.atMs + 4000 }));
  return clip.music.quranMute ? [...own, ...directed, ...clip.quranSpans] : [...own, ...directed];
}

/** Transcript words just outside the current trim: what the clip cut off at each end. */
export function contextWords(clip: Clip, transcriptWords: Word[] | null, windowMs: { before: number; after: number }): { before: Word[]; after: Word[] } {
  const { trimStartMs, trimEndMs } = editsOf(clip);
  const pool = transcriptWords && transcriptWords.length ? transcriptWords : clip.words;
  return {
    before: pool.filter((w) => w.endMs <= trimStartMs && w.endMs > trimStartMs - windowMs.before),
    after: pool.filter((w) => w.startMs >= trimEndMs && w.startMs < trimEndMs + windowMs.after),
  };
}

/** Pads a word boundary the same way "Start here" / "End here" do, so speech is never clipped. */
export const WORD_LEAD_MS = 150;
export const WORD_TAIL_MS = 250;

/** Grows the clip so it starts at (side "before") or ends at (side "after") the given transcript word, pulling the words in between from the transcript. The lead/tail padding never reaches into the neighbouring word. */
export function growToWord(clip: Clip, transcriptWords: Word[] | null, word: Word, side: "before" | "after"): Clip {
  const e = editsOf(clip);
  const pool = transcriptWords && transcriptWords.length ? transcriptWords : clip.words;
  const prev = pool.filter((w) => w.endMs <= word.startMs).reduce<Word | null>((a, w) => (!a || w.endMs > a.endMs ? w : a), null);
  const next = pool.filter((w) => w.startMs >= word.endMs).reduce<Word | null>((a, w) => (!a || w.startMs < a.startMs ? w : a), null);
  const lead = Math.max(0, Math.min(WORD_LEAD_MS, prev ? word.startMs - prev.endMs : WORD_LEAD_MS));
  const tail = Math.max(0, Math.min(WORD_TAIL_MS, next ? next.startMs - word.endMs : WORD_TAIL_MS));
  const trimStartMs = side === "before" ? Math.max(0, Math.min(e.trimStartMs, word.startMs - lead)) : e.trimStartMs;
  const trimEndMs = side === "after" ? Math.max(e.trimEndMs, word.endMs + tail) : e.trimEndMs;
  const have = new Set(clip.words.map((w) => w.startMs));
  const extra = [word, ...pool].filter((w) => !have.has(w.startMs) && w.endMs > trimStartMs - 3000 && w.startMs < trimEndMs + 3000);
  const seen = new Set<number>();
  const words = [...clip.words, ...extra].filter((w) => (seen.has(w.startMs) ? false : (seen.add(w.startMs), true))).sort((a, b) => a.startMs - b.startMs);
  return { ...clip, words, edits: { ...e, trimStartMs, trimEndMs } };
}

/** Puts a cut range back: manual cuts are trimmed around it and silences inside it are kept, so the words play again. */
export function restoreRange(clip: Clip, inMs: number, outMs: number): Clip {
  const e = editsOf(clip);
  const manualCuts: Segment[] = [];
  for (const m of e.manualCuts) {
    if (m.outMs <= inMs || m.inMs >= outMs) { manualCuts.push(m); continue; }
    if (m.inMs < inMs - 40) manualCuts.push({ inMs: m.inMs, outMs: inMs });
    if (m.outMs > outMs + 40) manualCuts.push({ inMs: outMs, outMs: m.outMs });
  }
  const kept = clip.silences.filter((s) => s.endMs > inMs && s.startMs < outMs).map((s) => ({ inMs: s.startMs, outMs: s.endMs }));
  const have = new Set(e.keptGaps.map((k) => `${k.inMs}:${k.outMs}`));
  const keptGaps = [...e.keptGaps, ...kept.filter((k) => !have.has(`${k.inMs}:${k.outMs}`))];
  return { ...clip, edits: { ...e, manualCuts, keptGaps } };
}
